import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
// GSAPのMotionPathPluginを登録
gsap.registerPlugin(MotionPathPlugin);
// 背景コンテナのスタイル設定
const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  background: black;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;
// キャンバスのスタイル
const StyledCanvas = styled.canvas`
  position: absolute;
  width: 100%;
  height: 100%;
`;

// テキストスタイル
const PositionedText = styled.div`
  position: absolute;
  color: rgba(0, 0, 0, 0.6);
  font-size: 2.5vmin;
  font-family: "'Nanum Myeongjo', serif";
  cursor: pointer;
  z-index: 1;
`;


// OpeningLayerコンポーネント
const OpeningLayer = ({ text = "Annelida", onSelect }) => {
  const canvasRef = useRef(null);
  const [initialized, setInitialized] = useState(false); // フラグを追加
  const activeCircles = useRef({ left: false, right: false }); // 左右のアニメーション状態を管理

  useEffect(() => {
    if (initialized) return; // 既に初期化されている場合は何もしない
    setInitialized(true); // 初期化フラグを立てる
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    // キャンバスサイズをウィンドウに合わせる
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const canvasCenter = { x: canvas.width / 2, y: canvas.height / 2 }; // 画面の中心座標
    const radiusN = Math.min(canvas.width, canvas.height) * 0.3; // 半径
    const animationRadius = 80; // グラデーション円の最大半径
    const detectionDistance = 50; // グラデーション円を広げる検出距離
    const randomOffset = (range) => (Math.random() - 0.5) * range; // -range/2 ～ +range/2 のオフセットを生成


    const rightTextPosition = {
      x: canvasCenter.x + radiusN,
      y: canvasCenter.y,
    };
    const leftTextPosition = {
      x: canvasCenter.x - radiusN,
      y: canvasCenter.y,
    };


    const dots = Array.from({ length: 2 }, () => {
      const initialAngle = Math.random() * Math.PI * 2; // ランダムな初期角度
      return {
        x: canvasCenter.x + (radiusN + Math.random() * 30) * Math.cos(initialAngle), // 初期位置X
        y: canvasCenter.y + (radiusN + Math.random() * 30) * Math.sin(initialAngle), // 初期位置Y
        angle: initialAngle, // 初期角度
        radius: 1, // ドットの半径
      };
    });
    // ドットごとのアニメーションを設定
    dots.forEach((dot) => {
      const midPoints = [];
      let currentAngle = dot.angle; // 現在の角度を初期角度に設定
      // 中間点を生成
      for (let i = 0; i < 1000; i++) {
        const angleOffset = (Math.random() * 30 + 10) * (Math.PI / 180); // ランダムに10〜30度を引く（ラジアンに変換）
        currentAngle -= angleOffset; // 角度を反時計回りに減少
        midPoints.push({
          x: canvasCenter.x + (radiusN + Math.random() * 40) * Math.cos(currentAngle), // 円周上のX座標
          y: canvasCenter.y + (radiusN + Math.random() * 40) * Math.sin(currentAngle), // 円周上のY座標
        });
      }
      // 始点を終点として追加してループを作成
      midPoints.push({ x: dot.x, y: dot.y });
      // GSAPでドットをアニメーション
      gsap.to(dot, {
        duration: 2000, // アニメーションの長さ（秒単位）
        motionPath: {
          path: [{ x: dot.x, y: dot.y }, ...midPoints], // 初期位置＋中間点＋始点
          curviness: 1.5, // 曲線の滑らかさ
          autoRotate: false, // 自動回転なし
        },
        ease: "none", // 等速移動
        repeat: -1, // 無限ループ
      });
    });

    const drawCircle = (center, maxRadius) => {
      const randomX = center.x + randomOffset(100); // X座標をランダムにずらす
      const randomY = center.y + randomOffset(100); // Y座標をランダムにずらす

      const gradient = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, maxRadius);
      gradient.addColorStop(0, "rgba(255, 255, 255, 0.5)");
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.beginPath();
      ctx.arc(center.x, center.y, maxRadius, 0, 2 * Math.PI);
      ctx.fillStyle = gradient;
      ctx.fill();
    };
    


    const draw = () => {
      // 背景をフェードアウトして軌跡を残す
      ctx.fillStyle = "rgba(0, 0, 0, 0.01)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // ドットを描画
      dots.forEach((dot, index) => {
        const time = Date.now() * 0.002; // 時間を基準に変動
        const dynamicRadius = dot.radius + Math.sin(time + index) * 0.5 + 1; // 半径をsinカーブで変動
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dynamicRadius, 0, 2 * Math.PI);
        ctx.fillStyle = "white";
        ctx.fill();


        // 動点とテキスト中心の距離を計算
        const distLeft = Math.hypot(dot.x - leftTextPosition.x, dot.y - leftTextPosition.y);
        const distRight = Math.hypot(dot.x - rightTextPosition.x, dot.y - rightTextPosition.y);

       // 検出距離内ならグラデーション円を描画
       if (distLeft < detectionDistance) drawCircle(leftTextPosition, animationRadius);
       if (distRight < detectionDistance) drawCircle(rightTextPosition, animationRadius);

      });
      requestAnimationFrame(draw);
    };
    draw(); // 描画開始
    // クリーンアップ
    return () => {
      cancelAnimationFrame(draw);
    };
  }, []); // 依存配列を空にして初回レンダリング時のみ実行

   // 円の右端と左端の座標を計算
   const canvasWidth = window.innerWidth;
   const canvasHeight = window.innerHeight;
   const canvasCenter = { x: canvasWidth / 2, y: canvasHeight / 2 };
   const radiusN = Math.min(canvasWidth, canvasHeight) * 0.3;
 
   const rightTextPosition = {
     x: canvasCenter.x + radiusN,
     y: canvasCenter.y,
   };
   const leftTextPosition = {
     x: canvasCenter.x - radiusN,
     y: canvasCenter.y,
   };


  return (
    <Wrapper onClick={onSelect}>
      <StyledCanvas ref={canvasRef} />
      <div
        style={{
          position: "absolute",
          color: "white",
          fontSize: "3.5vmin",
          fontFamily: "'Nanum Myeongjo', serif",
          cursor: "pointer",
          zIndex: 1,
        }}
      >
        {text}
      </div>

      {/* 左端のテキスト */}
    <PositionedText
        style={{
          left: `${leftTextPosition.x}px`,
          top: `${leftTextPosition.y}px`,
          transform: "translate(-50%, -50%)",
          fontFamily: "'Nanum Myeongjo', serif",
        }}
        onClick={() => onSelect("suburban")}
      >
        {"suburban"}
      </PositionedText>
      <PositionedText
        style={{
          left: `${rightTextPosition.x}px`,
          top: `${rightTextPosition.y}px`,
          transform: "translate(-50%, -50%)",
          fontFamily: "'Nanum Myeongjo', serif",
        }}
        onClick={() => onSelect("urban")}
      >
        {"urban"}
      </PositionedText>

    </Wrapper>
  );
};
export default OpeningLayer;